import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowIcon from '@mui/icons-material/ArrowForwardOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';

import { useLector, MONEDA, PERIODO } from '../config/lector';
import { whatsappValido } from '../config/sesion';
import { getSiteConfig, getSiteLanguages } from '../config/sites';
import { LANGUAGE_NAMES } from '../i18n';
import { useTranslation } from 'react-i18next';
import './cuenta.css';

/**
 * MI CUENTA — las siete secciones de DEFINICION_CUENTA.md §2.
 *
 * ⭐ EL ORDEN NO ES CASUAL: primero el perfil, porque el nombre es lo que cambia
 * cómo se lee el sitio; después el plan; y al final, sin esconderlo, cancelar y
 * borrar la cuenta. Una fundación no puede diseñar trampas para retener a quien
 * quiere irse — y además, que la salida esté a la vista es lo que hace que la
 * gente se atreva a entrar.
 *
 * ⚠️ LO QUE NO ESTÁ, Y NO ESTARÁ HASTA QUE HAYA SERVIDOR:
 *   · La forma de pago. Los datos de la tarjeta los escribe la pasarela en su
 *     propio campo; el sitio no los ve nunca (PCI-DSS, DEFINICION_CUENTA §2③).
 *   · El historial de pagos y las facturas, que los emite quien cobra.
 *   · Cambiar de plan de verdad, que es una operación de cobro.
 * Se enseña el hueco con lo que hará, en vez de fingir que funciona.
 */

const CuentaPage = () => {
  const { t } = useTranslation();
  const { lector, plan, numeroPlan, identificado, fijar, salir, borrarCuenta, exportarDatos } = useLector();
  const sitio = getSiteConfig();

  const [perfil, setPerfil] = useState({
    nombre: lector.nombre || '',
    genero: lector.genero || 'm',
    idioma: lector.idioma || 'es',
    /* El WhatsApp es opcional: vacio = no recibe el mensaje del dia por ahi.
       La casilla refleja si hay numero; desmarcarla lo borra al guardar. */
    whatsapp: lector.whatsapp || ''
  });
  const [quiereWhatsapp, setQuiereWhatsapp] = useState(!!lector.whatsapp);
  const [errorPerfil, setErrorPerfil] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [errorAccion, setErrorAccion] = useState('');

  useEffect(() => {
    setPerfil({
      nombre: lector.nombre || '',
      genero: lector.genero || 'm',
      idioma: lector.idioma || 'es',
      whatsapp: lector.whatsapp || ''
    });
    setQuiereWhatsapp(Boolean(lector.whatsapp));
  }, [lector.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!identificado) {
    return (
      <Box className="cue">
        <Container maxWidth="sm">
          <Typography variant="h1" className="cue-h1">{t('cuenta.titulo')}</Typography>
          <Typography className="cue-parrafo">
            {t('cuenta.no_has_entrado')}
          </Typography>
          <RouterLink to="/entrar" className="cue-boton-enlace">
            {t('cuenta.entrar_o_crear')} <ArrowIcon fontSize="inherit" />
          </RouterLink>
        </Container>
      </Box>
    );
  }

  const guardarPerfil = async (e) => {
    e.preventDefault();
    setErrorPerfil('');
    if (quiereWhatsapp && !whatsappValido(perfil.whatsapp)) {
      setErrorPerfil(t('cuenta.whatsapp_error'));
      return;
    }
    const result = await fijar({
      ...perfil,
      whatsapp: quiereWhatsapp ? perfil.whatsapp : ''
    });
    if (!result || !result.ok) {
      setErrorPerfil(t('cuenta.error_accion'));
      return;
    }
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2600);
  };

  const descargar = async () => {
    setErrorAccion('');
    const exported = await exportarDatos();
    if (!exported) {
      setErrorAccion(t('cuenta.error_accion'));
      return;
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mis-datos-jeshua.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const confirmarBorrado = async () => {
    const result = await borrarCuenta();
    setBorrando(false);
    if (!result.ok) setErrorAccion(t('cuenta.error_accion'));
  };

  /* Los que hay, no los 33 declarados: mismo motivo que en el registro. */
  const idiomas = getSiteLanguages(sitio);

  return (
    <Box className="cue">
      <Container maxWidth="md">

        {/* 🔴 ABRE CON SU NOMBRE, no con «Tu cuenta» (29-ago). En un sitio
            donde el texto llama al lector por su nombre, su propia pagina no
            puede recibirle con el rotulo de una pantalla de banco. El correo
            queda debajo, pequeño, que es lo que sirve para reconocer CUAL de
            sus cuentas es — y el plan a la derecha, porque es el dato que se
            viene a mirar. */}
        <Box className="cue-cabecera">
          <Box>
            <Typography variant="h1" className="cue-h1">
              {(lector.nombre || '').trim() || t('cuenta.titulo')}
            </Typography>
            <Typography className="cue-quien">{lector.correo}</Typography>
          </Box>
          <span className="cue-chip">{t(plan.clave)}</span>
        </Box>

        {/* 🔴 DOS COLUMNAS (29-ago): lo que se EDITA a la izquierda, lo que
            TIENES a la derecha. Eran seis bloques en una columna, todos del
            mismo peso, y habia que bajar la pagina entera sin jerarquia; asi
            las dos cosas que de verdad hacen falta caben en una pantalla.
            Debajo de 900 px vuelve a una sola columna. */}
        <Box className="cue-dos">

        {/* ① PERFIL */}
        <Box className="cue-bloque">
          <Typography className="cue-rotulo">{t('cuenta.tu_perfil')}</Typography>
          <Box component="form" onSubmit={guardarPerfil} className="cue-campos">
            <TextField
              label={t('contacto.tu_nombre')} value={perfil.nombre} fullWidth variant="outlined"
              onChange={(e) => setPerfil({ ...perfil, nombre: e.target.value })}
              helperText={t('cuenta.nombre_ayuda')}
            />
            <Box className="cue-genero">
              <Typography className="cue-etiqueta">{t('cuenta.como_te_hablen')}</Typography>
              <Box className="cue-opciones">
                {[
                  { v: 'm', t: t('cuenta.genero_m') },
                  { v: 'f', t: t('cuenta.genero_f') },
                  { v: 'n', t: t('cuenta.genero_n') }
                ].map((g) => (
                  <button
                    type="button" key={g.v}
                    className={`cue-opcion${perfil.genero === g.v ? ' cue-opcion-activa' : ''}`}
                    onClick={() => setPerfil({ ...perfil, genero: g.v })}
                  >
                    {g.t}
                  </button>
                ))}
              </Box>
            </Box>
            {/* No se pregunta lo que no se puede elegir: con un solo idioma
                este desplegable no decide nada. Igual que en el registro. */}
            {idiomas.length > 1 && (
              <TextField
                select SelectProps={{ native: true }} label="Idioma"
                value={perfil.idioma} fullWidth variant="outlined"
                onChange={(e) => setPerfil({ ...perfil, idioma: e.target.value })}
              >
                {idiomas.map((l) => <option key={l} value={l}>{LANGUAGE_NAMES[l] || l}</option>)}
              </TextField>
            )}

            {/* ⑥ AVISOS, dentro del perfil: el mensaje del dia por WhatsApp.
                Opcional, con su porque, y el numero solo si lo pide — igual
                que en el registro (Faina, 11-sep). Quitar la casilla y guardar
                es darse de baja: no hay otro sitio donde haya que ir. */}
            <Box className="cue-whatsapp">
              <label className="cue-casilla">
                <input
                  type="checkbox"
                  checked={quiereWhatsapp}
                  onChange={(e) => setQuiereWhatsapp(e.target.checked)}
                />
                <span>{t('cuenta.recibir_whatsapp')}</span>
              </label>
              {quiereWhatsapp && (
                <TextField
                  label={t('cuenta.tu_whatsapp')} type="tel" value={perfil.whatsapp} fullWidth variant="outlined"
                  autoComplete="tel" inputProps={{ inputMode: 'tel' }}
                  onChange={(e) => setPerfil({ ...perfil, whatsapp: e.target.value })}
                  helperText={t('cuenta.whatsapp_ayuda')}
                />
              )}
            </Box>
            {errorPerfil && <Alert severity="error">{errorPerfil}</Alert>}
            <Box className="cue-acciones">
              <Button type="submit" className="cue-guardar">{t('cuenta.guardar')}</Button>
              {guardado && <span className="cue-guardado">{t('cuenta.guardado')}</span>}
            </Box>
          </Box>
        </Box>

        {/* ② PLAN */}
        <Box className="cue-bloque cue-bloque-plan">
          <Typography className="cue-rotulo">{t('cuenta.tu_plan')}</Typography>
          <Box className="cue-plan">
            <Box>
              <Typography className="cue-plan-nombre">{t(plan.clave)}</Typography>
              <Typography className="cue-plan-precio">
                {plan.precio === 0 ? t('home.plan_gratis') : `${plan.precio} ${MONEDA} ${t('home.plan_periodo')}`}
              </Typography>
            </Box>
          </Box>

          {/* 🔴 SEPARADAS, NO MEZCLADAS (29-ago). Los siete permisos estaban en
              una rejilla de tres columnas, y el unico que SI tienes se perdia
              entre los seis que no. Partidos en dos listas con su rotulo, se ve
              de un vistazo que tienes hoy y que te daria pagar — que es
              exactamente la pregunta que trae a alguien a esta pagina. */}
          {(() => {
            const TODO = [
              [t('cuenta.tienes_leer'), plan.leerCanalizaciones],
              [t('cuenta.tienes_nombre'), plan.personaliza],
              [t('cuenta.tienes_imprimir'), plan.imprimir],
              [t('cuenta.tienes_musica'), plan.musica],
              [t('cuenta.tienes_audio'), plan.audioPersonal],
              [t('cuenta.tienes_ensenanzas'), plan.ensenanzas],
              /* 🚪 Sin intranet (Faina, 07-10-2026): fuera «lo interno» y «el
                 acceso interno». El plan 4 trae las de terceros (Def 100). */
              [t('cuenta.tienes_terceros'), plan.corpusTerceros]
            ];
            const tienes = TODO.filter(([, s_]) => s_);
            const faltan = TODO.filter(([, s_]) => !s_);
            return (
              <>
                {tienes.length > 0 && (
                  <>
                    <Typography className="cue-mini">{t('cuenta.lo_que_tienes')}</Typography>
                    <Box component="ul" className="cue-lista">
                      {tienes.map(([que]) => (
                        <li key={que} className="cue-si">{que}</li>
                      ))}
                    </Box>
                  </>
                )}
                {faltan.length > 0 && (
                  <>
                    <Typography className="cue-mini cue-mini-sep">{t('cuenta.con_plan_pago')}</Typography>
                    <Box component="ul" className="cue-lista">
                      {faltan.map(([que]) => (
                        <li key={que} className="cue-no">
                          <LockIcon fontSize="inherit" /> {que}
                        </li>
                      ))}
                    </Box>
                  </>
                )}
              </>
            );
          })()}

          {numeroPlan < 4 && (
            <RouterLink to="/#planes" className="cue-plan-cambiar">{t('cuenta.ver_planes')}</RouterLink>
          )}

          {numeroPlan > 1 && (
            <Typography className="cue-cancelar-nota">
              {t('cuenta.cancelar_nota')}
            </Typography>
          )}

          {/* 🔴 LAS DOS SECCIONES VACIAS, RETIRADAS (29-ago). «Forma de pago» y
              «Mi biblioteca» ocupaban el mismo peso que las que funcionan y solo
              decian «disponible cuando…» y «en construccion»: la cuenta parecia
              a medio hacer. Vuelven cuando existan.
              Lo unico que valia la pena de ahi —que el sitio no guarda la
              tarjeta— baja aqui a una linea: tranquiliza igual y no finge una
              seccion. */}
          <Typography className="cue-pie-plan">
            {t('cuenta.pagos_nota')}
          </Typography>
        </Box>

        </Box>{/* /cue-dos */}

        {/* ⑤ PRIVACIDAD */}
        <Box className="cue-bloque">
          <Typography className="cue-rotulo">{t('cuenta.tus_datos')}</Typography>
          <Typography className="cue-parrafo">
            {t('cuenta.datos_nota')}
          </Typography>
          <Box className="cue-acciones">
            <Button className="cue-secundario" onClick={descargar}>{t('cuenta.descargar')}</Button>
            <Button className="cue-peligro" onClick={() => setBorrando(true)}>{t('cuenta.borrar')}</Button>
          </Box>
          {errorAccion && <Alert severity="error">{errorAccion}</Alert>}
        </Box>

        {/* ⑥ SALIR */}
        <Box className="cue-bloque cue-bloque-final">
          <Button className="cue-secundario" onClick={salir}>
            {t('cuenta.cerrar_sesion')}
          </Button>
        </Box>

        <Dialog open={borrando} onClose={() => setBorrando(false)}>
          <DialogTitle>{t('cuenta.borrar_pregunta')}</DialogTitle>
          <DialogContent>
            <Typography className="cue-parrafo">
              {t('cuenta.borrar_texto_1')}
            </Typography>
            <Typography className="cue-parrafo">
              {t('cuenta.borrar_texto_2')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBorrando(false)}>{t('cuenta.quedarme')}</Button>
            <Button className="cue-peligro" onClick={confirmarBorrado}>{t('cuenta.si_borrarla')}</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default CuentaPage;
